import { CoverageLevelEnum, PeriodUnitEnum } from './eligibility.model';

import { AppConstants } from '../constants/app.constants';
import { GetAppointmentByIdResponse } from './responses';
import { GetEligibilityByAppointmentIdResponse } from './responses/eligibility.response';

export enum AnswerTypeEnum {
  Boolean = 'Boolean',
  ShortAnswer = 'Short Answer',
  // LongAnswer = 'Long Answer',
  Number = 'Number',
  Date = 'Date',
  Limitation = 'Limitation',
  Maximum = 'Maximum',
  Deductible = 'Deductible'
}

export const AnswerTypesMap: { [key: string]: string } = {
  [AnswerTypeEnum.Boolean]: 'Yes or No',
  [AnswerTypeEnum.ShortAnswer]: AnswerTypeEnum.ShortAnswer,
  // [AnswerTypeEnum.LongAnswer]: AnswerTypeEnum.LongAnswer,
  [AnswerTypeEnum.Number]: AnswerTypeEnum.Number,
  [AnswerTypeEnum.Date]: AnswerTypeEnum.Date,
  [AnswerTypeEnum.Limitation]: AnswerTypeEnum.Limitation,
  [AnswerTypeEnum.Maximum]: AnswerTypeEnum.Maximum,
  [AnswerTypeEnum.Deductible]: AnswerTypeEnum.Deductible
};

export const PossibleAnswerTypes = Object.entries(AnswerTypesMap).map(([key, value]) => ({
  name: value,
  value: key
}));

export const CoverageLevels = Object.entries(CoverageLevelEnum)
  .filter((level) => level[1] !== CoverageLevelEnum.unknown)
  .map(([key, value]) => ({
    name: AppConstants.coverageLevelMap[value],
    value: AppConstants.coverageLevelMap[key]
  }));

export type FieldIdentifierPayload = {
  type: QuestionFieldType;
  category?: string;
  subject: QuestionFieldSubjectEnum;
  code?: string | null;
  period?: string | null;
  networkName?: string | null;
  networkType?: ProviderNetworkType | null;
  coverageLevel?: string | null;
  notes?: string | null;
  id?: number | null;
};

export type QuestionField = FieldIdentifierPayload & {
  originalAnswer?: string | null;
};

export type QuestionModal = {
  id?: number;
  isOpen: boolean;
  isLoading: boolean;
  isCustomQuestion: boolean;
  field?: QuestionField;
  isTemplateMode?: boolean;
  selectedQuestionId?: number | string;
  customQuestion: { question: string; answerType: string };
  suggestedQuestions?: { id: number; question: string; answerType: string }[];
  allAddedQuestions?: Question[];
  isWildCardMode?: boolean;
  openFields?: {
    category: string;
  };
};

export type AnswerHistory = {
  agentAnswer: string;
  agentAnswerTypeOverride: null;
  answerStatus: string;
  supportUserId: number;
  createdAt: string;
  updatedAt: string;
  supportUser: {
    supportUserId: number;
    firstName: string;
    lastName: string;
  };
};

export type Question = {
  questionId: number;
  id: number;
  questionType: QuestionTypeEnum;
  question: string;
  answerType: string;
  originalAnswer: string;
  questionField: FieldIdentifierPayload;
  answerAccepted?: boolean;
  answerHistory: AnswerHistory[];
};

export enum ProviderNetworkType {
  InNetwork = 'In Network',
  OutOfNetwork = 'Out of Network',
  InAndOutOfNetwork = 'In and Out of Network'
}

export enum AnswerStatusEnum {
  NotAnswered = 'Not Answered',
  Answered = 'Answered',
  UnableToAnswer = 'Unable to Answer',
  FollowUp = 'Follow Up',
  Overridden = 'Overridden'
}

export enum QuestionFieldCoverageLevelEnum {
  Individual = 'Individual',
  Family = 'Family'
}

export enum QuestionFieldPeriodEnum {
  Year = 'YEAR',
  Month = 'MONTH',
  Week = 'WEEK',
  Day = 'DAY'
}

export const QuestionFieldPeriodToEligibilityMap: { [key: string]: string } = {
  [QuestionFieldPeriodEnum.Year]: PeriodUnitEnum.Year,
  [QuestionFieldPeriodEnum.Month]: PeriodUnitEnum.Month,
  [QuestionFieldPeriodEnum.Day]: PeriodUnitEnum.Day
};

export enum QuestionFieldSubjectEnum {
  PayerCoverage = 'Payer Coverage',
  CoInsurance = 'Co Insurance',
  CoPay = 'Co Pay',
  AgeMin = 'Age Min',
  AgeMax = 'Age Max',
  Limitation = 'Limitation',
  Amount = 'Amount',
  Remaining = 'Remaining',
  Period = 'Period',
  Waiting = 'Waiting',
  Date = 'Date',
  Notes = 'Notes',
  PeriodAmountRemaining = 'Period - Amount - Remaining'
}

export const MaxDedSubjectOptions: { [key: string]: string } = {
  [QuestionFieldSubjectEnum.Amount]: QuestionFieldSubjectEnum.Amount,
  [QuestionFieldSubjectEnum.Remaining]: QuestionFieldSubjectEnum.Remaining
};

export const MaxDedSubjectOptionsMap = Object.entries(MaxDedSubjectOptions).map(([key, value]) => ({
  name: value,
  value: key
}));

export const customQuestionId = 'CUSTOM_QUESTION';

export enum QuestionFieldType {
  Maximums = 'Maximums',
  Deductibles = 'Deductibles',
  Benefits = 'Benefits',
  Notes = 'Notes',
  ServiceHistory = 'Service History'
}

export enum FieldIdentificationEnum {
  CoPay = 'CO_PAY',
  CoInsurance = 'CO_INSURANCE',
  PayerCoverage = 'PAYER_COVERAGE',
  AgeMinimum = 'AGE_MINIMUM',
  AgeMaximum = 'AGE_MAXIMUM',
  VisitLimit = 'VISIT_LIMIT',
  Period = 'PERIOD',
  Amount = 'Amount',
  Remaining = 'Remaining'
}

export enum EligibilityCompletePriority {
  Standard = 'Standard',
  High = 'High',
  Emergency = 'Emergency'
}

export enum EligibilityTemplateStatus {
  Active = 'Active',
  Inactive = 'Inactive'
}

export const EligibilityCompletePriorityMap = {
  [EligibilityCompletePriority.Standard]: EligibilityCompletePriority.Standard,
  [EligibilityCompletePriority.High]: EligibilityCompletePriority.High,
  [EligibilityCompletePriority.Emergency]: EligibilityCompletePriority.Emergency
};

export type CreateNewQuestionPayload = {
  questionField: QuestionField;
  question: string;
  questionId?: number;
  answerType: string;
  questionType: string;
  originalAnswer?: string | null;
  alwaysAsk?: boolean;
  customCategory?: string;
  customSubject?: string;
};

export type CreateNewQuestionResponse = {
  requestId: number;
  questionId: number;
  payerId?: null | number;
  groupId: string;
  questionFieldId: number;
  questionField: {
    id: number;
    type?: string | null;
    category: string;
    code?: null | string;
    subject?: null | string;
    period?: null | string;
    networkName?: null | string;
    networkType: ProviderNetworkType | null;
    coverageLevel?: null | string;
    notes?: string | null;
    updatedAt?: string | null;
    createdAt?: string | null;
    deletedAt?: null | string;
  };
  question: string;
  originalAnswer: string | '';
  answerType: AnswerTypeEnum;
  questionType: QuestionTypeEnum;
};

export type DeleteQuestionResponse = {
  requestQuestionId: number;
  questionid: number;
  requestId: number;
  deleted: boolean;
};

export type UpdateQuestionPayload = {
  questionId: number;
  question: string;
  answerType: string;
  questionType: string;
  originalAnswer?: string;
  alwaysAsk?: boolean;
};

export type UpdateQuestionResponse = {
  requestId: number;
  questionId: number;
  payerId?: number | null;
  groupId: string;
  questionFieldId: number;
  question: string;
  originalAnswer?: string | null;
  answerType: AnswerTypeEnum;
  questionType: QuestionTypeEnum;
};

export type GetRequestsByEligibilityIdResponse = {
  page: number;
  limit: number;
  total: number;
  results: EligibilityCompleteRequest[];
};

export type UpdatedRequestResponse = {
  requestId: number;
  eligibilityId: number;
  providerNetworkType: ProviderNetworkType;
  callType: EligibilityCompleteRequestType;
  priority: EligibilityCompletePriority;
  userId: number;
  status: EligibilityCompleteRequestStatus;
  agentStatus: EligibilityCompleteAgentStatus;
  sla: null;
  assignedAgentId: null;
  submitted: string | null;
};

export enum QuestionTypeEnum {
  Suggested = 'Suggested',
  Custom = 'Custom'
}

export enum EligibilityCompleteRequestStatus {
  Created = 'Created',
  Queued = 'Queued',
  Review = 'Review',
  Complete = 'Complete',
  Error = 'Error'
}

export const EligibilityCompleteRequestStatusMap = {
  [EligibilityCompleteRequestStatus.Created]: 'Created',
  [EligibilityCompleteRequestStatus.Queued]: 'Queued',
  [EligibilityCompleteRequestStatus.Review]: 'Pending Review',
  [EligibilityCompleteRequestStatus.Complete]: 'Complete',
  [EligibilityCompleteRequestStatus.Error]: 'Error'
};

export enum EligibilityCompleteRequestType {
  Call = 'Manual',
  AiCall = 'AiCall'
}

export enum AgentStatus {
  Completed = 'Completed'
}

export type EligibilityCompleteRequest = {
  requestId: number;
  appointmentId: number;
  eligibilityId: number;
  patientId: number;
  status: EligibilityCompleteRequestStatus;
  priority: EligibilityCompletePriority;
  callType: EligibilityCompleteRequestType;
  providerNetworkType: ProviderNetworkType;
  eligibility?: { id: number; appointmentId: number };
  submitted: boolean | null;
  agentStatus: AgentStatus | null;
  groupId: string;
  appointmentDate: string | null;
  patientFirstName: string;
  patientLastName: string;
  payerName: string;
  sla: string | null;
  assignedSupportUserId: number;
  assignedSupportUserFirstName: string;
  assignedSupportUserLastName: string;
  questionCount: number;
  questions?: ({ questionField: QuestionField } & Question)[];
  completed: boolean | null;
  created: Date;
  updated: Date;
};

export type EligibilityCompleteTemplate = {
  id: number;
  name: string;
  Payers: {
    name: string;
    id: number;
  }[];
  numberOfQuestions: number;
  priority: EligibilityCompletePriority;
  sameDayPriority: EligibilityCompletePriority;
  providerParticipation: ProviderNetworkType;
  inclusionType: PayerMapType;
  procedureCodes: string[];
  Questions?: ({ questionField: QuestionField } & Question)[];
  autoQueue: boolean;
  autoOverride: boolean;
  lastUsed: string;
  status: EligibilityTemplateStatus;
  createdAt: string;
  updatedAt: string;
};

export type EligibilityCompleteTemplateResponse = Omit<EligibilityCompleteTemplate, 'Payers'> & {
  Payers: {
    payerName: string;
    payerId: number;
  }[];
};

export interface GetEligibilityRequestsPayload {
  page: number;
  limit: number;
  eligibilityId: number;
}

export enum EligibilityCompleteAgentStatus {
  Queued = 'Queued'
}

export enum PayerMapType {
  All = 'All',
  Include = 'Include',
  Exclude = 'Exclude'
}

export interface SandboxDataResponse {
  data: GetAppointmentByIdResponse & { eligibility: GetEligibilityByAppointmentIdResponse };
  description: string | null;
  groupId: string | null;
  id: number;
  name: string;
  payerId: number | null;
  version: string | null;
}

export interface CreateNewTemplateResponse {
  id: number;
  groupId: string;
  name: string;
  priority: EligibilityCompletePriority;
  status: EligibilityTemplateStatus;
  autoQueue: boolean;
  autoOverride: boolean;
  inclusionType: PayerMapType;
  sandboxDataId: number;
  updatedAt: string;
  createdAt: string;
  deletedAt: string | null;
}

export interface CreateNewTemplatePayload {
  name: string;
  payerMapType: PayerMapType;
  priority: EligibilityCompletePriority;
  status: EligibilityTemplateStatus;
  autoQueue: boolean;
  autoOverride: boolean;
  payerIds: number[];
  sandboxDataId?: number;
  providerParticipation: ProviderNetworkType;
  sameDayPriority: EligibilityCompletePriority;
}

export interface EnhancedPayer {
  payerId: number;
  name: string;
}

export const AppointmentSandboxId = 'sandbox-appointment';
export const PatientSandboxId = 'sandbox-patient';
